import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import ModalBase from '@/components/base/ModalBase';
import type { SARadio, CommunityStats } from '@/mocks/superAdminRadios';
import { StatusBadge, StatusTimeline } from './StatusChangeModal';
import { radioContracts } from '@/mocks/superAdminContracts';
import { invoices } from '@/mocks/superAdminInvoices';
import { payments } from '@/mocks/superAdminPayments';
import { subscriptions } from '@/mocks/superAdminBilling';
import { radioEconomyScores, getEconomyScoreState } from '@/mocks/radioEconomyScores';
import RadioEconomyScore from '@/components/feature/RadioEconomyScore';
import type { EconomyScoreFactors } from '@/mocks/radioEconomyScores';

interface RadioDetailModalProps {
  radio: SARadio | null;
  onClose: () => void;
  onSuspend: (radio: SARadio) => void;
  onReactivate: (radio: SARadio) => void;
  onChangePlan: (radio: SARadio) => void;
}

type ModalTab = 'resumen' | 'comunidad' | 'facturacion' | 'contrato' | 'actividad';

const planFullNames: Record<string, string> = { free: 'Starter (Free)', pro: 'Pro', premium: 'Premium' };

const planLabels: Record<string, { label: string; color: string; bg: string }> = {
  free: { label: 'Free', color: 'text-secondary-600', bg: 'bg-secondary-100' },
  pro: { label: 'Pro', color: 'text-primary-600', bg: 'bg-primary-100' },
  premium: { label: 'Premium', color: 'text-accent-600', bg: 'bg-accent-100' },
};

const billingStatusLabels: Record<string, { label: string; color: string; icon: string }> = {
  trial: { label: 'Trial', color: 'text-sky-600 bg-sky-50', icon: 'ri-flashlight-line' },
  active: { label: 'Activa', color: 'text-emerald-600 bg-emerald-50', icon: 'ri-check-line' },
  past_due: { label: 'Past Due', color: 'text-red-500 bg-red-50', icon: 'ri-alert-line' },
  grace_period: { label: 'Grace Period', color: 'text-amber-600 bg-amber-50', icon: 'ri-hourglass-line' },
  suspended: { label: 'Suspendida', color: 'text-amber-600 bg-amber-50', icon: 'ri-pause-circle-line' },
  cancelled: { label: 'Cancelada', color: 'text-foreground-500 bg-foreground-100', icon: 'ri-forbid-line' },
};

const tabs: { id: ModalTab; label: string; icon: string }[] = [
  { id: 'resumen', label: 'Resumen', icon: 'ri-information-line' },
  { id: 'comunidad', label: 'Comunidad', icon: 'ri-group-line' },
  { id: 'facturacion', label: 'Facturación', icon: 'ri-bank-card-line' },
  { id: 'contrato', label: 'Contrato', icon: 'ri-file-text-line' },
  { id: 'actividad', label: 'Actividad', icon: 'ri-history-line' },
];

function CommunityStatCard({ icon, label, value, color }: { icon: string; label: string; value: number; color: string }) {
  return (
    <div className="bg-background-50 rounded-xl p-3 border border-background-200">
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center">
          <i className={`${icon} ${color} text-xs`}></i>
        </div>
        <span className="text-foreground-400 text-[10px] font-medium">{label}</span>
      </div>
      <p className="text-foreground-800 text-lg font-bold">{value.toLocaleString()}</p>
    </div>
  );
}

function DetailField({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="bg-background-100 rounded-xl p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <i className={`${icon} text-foreground-400 text-[11px]`}></i>
        <span className="text-foreground-400 text-[10px] font-medium">{label}</span>
      </div>
      <p className="text-foreground-800 text-xs font-semibold truncate">{value}</p>
    </div>
  );
}

export default function RadioDetailModal({ radio, onClose, onSuspend, onReactivate, onChangePlan }: RadioDetailModalProps) {
  const [activeTab, setActiveTab] = useState<ModalTab>('resumen');
  const [urlCopied, setUrlCopied] = useState(false);
  const navigate = useNavigate();

  if (!radio) return null;

  const plan = planLabels[radio.plan];
  const storagePercent = Math.round((radio.storageUsedGB / radio.storageTotalGB) * 100);
  const qrUrl = `https://radiofrecuenciaweb.com/r/${radio.redirectCode}`;

  const isSuspended = radio.status === 'suspendida';
  const isVencida = radio.status === 'vencida';
  const isPending = radio.status === 'solicitud_recibida' || radio.status === 'en_revision';
  const isCanceled = radio.status === 'cancelada' || radio.status === 'archivada';

  const relatedContract = radioContracts.find((c) => c.radioName.toLowerCase() === radio.name.toLowerCase());
  const relatedInvoices = invoices.filter((inv) => inv.radioName.toLowerCase() === radio.name.toLowerCase());
  const relatedPayments = payments.filter((p) => p.radioName.toLowerCase() === radio.name.toLowerCase());
  const relatedBilling = subscriptions.find((s) => s.radioName.toLowerCase() === radio.name.toLowerCase());

  const lastPayment = relatedPayments.length > 0
    ? relatedPayments.reduce((latest, p) => (p.date > latest.date ? p : latest), relatedPayments[0])
    : null;
  const currentInvoice = relatedInvoices.length > 0
    ? relatedInvoices.reduce((latest, inv) => (inv.issuedAt > latest.issuedAt ? inv : latest), relatedInvoices[0])
    : null;

  const stats: CommunityStats = radio.communityStats || { followers: 0, posts: 0, comments: 0, events: 0, interactions: 0 };

  const streamStatusInfo = radio.streamStatus === 'activo'
    ? { text: 'Stream Live', color: 'bg-emerald-50 text-emerald-600', dot: 'bg-emerald-500' }
    : radio.streamStatus === 'caido'
      ? { text: 'Stream Caído', color: 'bg-red-50 text-red-500', dot: 'bg-red-500' }
      : { text: 'Stream Off', color: 'bg-stone-50 text-stone-500', dot: 'bg-stone-400' };

  const handleCopyQr = () => {
    navigator.clipboard.writeText(qrUrl).then(
      () => { setUrlCopied(true); setTimeout(() => setUrlCopied(false), 2000); },
    );
  };

  return (
    <ModalBase
      open={true}
      onClose={onClose}
      title="Detalle de Radio"
      subtitle={`${radio.name} · ${radio.frequency}`}
      statusLabel={`${planFullNames[radio.plan] || plan.label} · ${radio.status.replace(/_/g, ' ')}`}
      statusColor={`${plan.bg} ${plan.color}`}
      size="xl"
      headerIcon={
        <div className="w-9 h-9 rounded-xl bg-background-100 overflow-hidden shrink-0">
          <img src={radio.logoUrl} alt={radio.name} className="w-full h-full object-cover" />
        </div>
      }
      footer={
        <div className="flex items-center gap-2 flex-wrap w-full">
          {/* Acciones siempre visibles */}
          <button
            onClick={() => { navigate(`/radio/${radio.redirectCode}`); onClose(); }}
            className="px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-external-link-line text-xs"></i>Ver radio
          </button>
          <button
            onClick={() => { navigate('/sa/contracts'); onClose(); }}
            className="px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-file-text-line text-xs"></i>Ver contrato
          </button>
          <button
            onClick={() => { navigate('/sa/invoices'); onClose(); }}
            className="px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-file-list-3-line text-xs"></i>Ver invoices
          </button>
          <button
            onClick={() => { navigate('/sa/payments'); onClose(); }}
            className="px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-money-dollar-circle-line text-xs"></i>Ver pagos
          </button>

          {/* Separador */}
          <span className="w-px h-6 bg-background-200 mx-1 hidden sm:block"></span>

          {/* QR */}
          <div className="relative">
            <button
              onClick={() => {
                Swal.fire({
                  title: `QR · ${radio.name}`,
                  html: `<div style="text-align:center"><img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}&bgcolor=ffffff&color=000000" alt="QR" style="width:160px;height:160px;border-radius:12px;margin-bottom:12px" /><p style="font-size:11px;color:#6b7280;font-family:monospace">/r/${radio.redirectCode}</p></div>`,
                  confirmButtonText: 'Cerrar',
                  confirmButtonColor: '#1f2937',
                  customClass: { popup: 'rounded-2xl', title: 'font-bold text-sm', confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap' },
                  buttonsStyling: false,
                  showCancelButton: true,
                  cancelButtonText: 'Copiar URL',
                  cancelButtonColor: '#6b7280',
                }).then((r) => { if (r.isDismissed) handleCopyQr(); });
              }}
              className="px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-qr-code-line text-xs"></i>Ver QR
            </button>
            {urlCopied && (
              <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-[9px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                URL copiada
              </span>
            )}
          </div>

          {/* Ver frecuencia */}
          <button
            onClick={() => { navigate('/sa/frequencies'); onClose(); }}
            className="px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-radar-line text-xs"></i>Ver frecuencia
          </button>

          {/* Cambiar plan - only for non-pending radios */}
          {!isPending && (
            <button
              onClick={() => { onChangePlan(radio); onClose(); }}
              className="px-3 py-2 rounded-xl bg-amber-500 text-background-50 text-xs font-bold hover:bg-amber-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-arrow-up-circle-line text-xs"></i>Cambiar plan
            </button>
          )}

          {/* Suspender / Reactivar */}
          {isSuspended || isVencida ? (
            <button
              onClick={() => { onReactivate(radio); onClose(); }}
              className="px-3 py-2 rounded-xl bg-emerald-500 text-background-50 text-xs font-bold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-play-circle-line text-xs"></i>Reactivar
            </button>
          ) : !isPending && !isCanceled && (
            <button
              onClick={() => { onSuspend(radio); onClose(); }}
              className="px-3 py-2 rounded-xl bg-red-500 text-background-50 text-xs font-bold hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-pause-circle-line text-xs"></i>Suspender
            </button>
          )}

          <button
            onClick={onClose}
            className="px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap ml-auto"
          >
            Cerrar
          </button>
        </div>
      }
    >
      {/* Status row */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${plan.color} ${plan.bg}`}>{planFullNames[radio.plan] || plan.label}</span>
        <StatusBadge status={radio.status} size="sm" />
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${streamStatusInfo.color}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${streamStatusInfo.dot}`}></span>
          {streamStatusInfo.text}
        </span>
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-[11px] font-bold text-emerald-600">Score {radio.radioScore.score}</span>
          {(() => {
            const ecoData = radioEconomyScores[radio.id];
            if (!ecoData) return null;
            return (
              <div className="flex items-center gap-1.5">
                <span className="text-foreground-300 text-[14px]">·</span>
                <span className="text-[11px] font-bold text-amber-600">Economy {ecoData.score}</span>
              </div>
            );
          })()}
        </div>
      </div>

      {/* TABS */}
      <div className="flex items-center bg-background-100 rounded-xl p-0.5 mb-5 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.id ? 'bg-foreground-950 text-background-50' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${tab.icon} mr-1`}></i>{tab.label}
          </button>
        ))}
      </div>

      {/* ══ RESUMEN ══ */}
      {activeTab === 'resumen' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <DetailField icon="ri-map-pin-line" label="Ciudad" value={radio.city} />
            <DetailField icon="ri-earth-line" label="País" value={radio.country} />
            <DetailField icon="ri-folder-music-line" label="Categoría" value={radio.category} />
            <DetailField icon="ri-headphone-line" label="Oyentes" value={radio.listeners.toLocaleString()} />
            <DetailField icon="ri-calendar-line" label="Registro" value={radio.registeredAt} />
            <DetailField icon="ri-user-line" label="Dueño" value={radio.ownerName} />
          </div>

          <div className="bg-background-100 rounded-xl p-3">
            <p className="text-foreground-400 text-[10px] font-medium mb-1">Eslogan</p>
            <p className="text-foreground-800 text-xs italic">"{radio.slogan}"</p>
          </div>

          {/* ── Economy Score Breakdown ── */}
          {(() => {
            const ecoData = radioEconomyScores[radio.id];
            if (!ecoData) return null;
            const factorLabels: Record<keyof EconomyScoreFactors, { label: string; icon: string }> = {
              seguidores: { label: 'Seguidores', icon: 'ri-user-heart-line' },
              oyentes: { label: 'Oyentes', icon: 'ri-headphone-line' },
              interaccion: { label: 'Interacción', icon: 'ri-heart-line' },
              patrocinadores: { label: 'Patrocinadores', icon: 'ri-hand-heart-line' },
              campañas: { label: 'Campañas', icon: 'ri-megaphone-line' },
              eventos: { label: 'Eventos', icon: 'ri-calendar-event-line' },
            };
            return (
              <div className="bg-background-50 border border-background-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center">
                      <i className="ri-funds-line text-amber-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-foreground-800 text-xs font-bold">Economy Score</p>
                      <p className="text-foreground-400 text-[9px]">Potencial Comercial</p>
                    </div>
                  </div>
                  <RadioEconomyScore
                    score={ecoData.score}
                    factors={ecoData.factors}
                    size="xs"
                    showLabel={false}
                    clickable={true}
                    radioName={radio.name}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {(Object.entries(ecoData.factors) as [keyof EconomyScoreFactors, number][]).map(([key, val]) => {
                    const fl = factorLabels[key];
                    const fState = getEconomyScoreState(val);
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-background-200 flex items-center justify-center shrink-0">
                          <i className={`${fl.icon} text-foreground-500 text-[10px]`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-foreground-500 text-[9px] truncate">{fl.label}</span>
                            <span className="text-foreground-700 text-[9px] font-bold">{val}</span>
                          </div>
                          <div className="h-1 bg-background-200 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${fState === 'excelente' || fState === 'alto' ? 'bg-emerald-500' : fState === 'moderado' ? 'bg-amber-500' : fState === 'bajo' ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${val}%` }}></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-3 text-foreground-400 text-[9px] pt-1 border-t border-background-200">
                  <span><i className="ri-hand-heart-line mr-1"></i>{ecoData.sponsorsCount} patrocinador{ecoData.sponsorsCount !== 1 ? 'es' : ''}</span>
                  <span><i className="ri-megaphone-line mr-1"></i>{ecoData.activeCampaigns} campaña{ecoData.activeCampaigns !== 1 ? 's' : ''} activa{ecoData.activeCampaigns !== 1 ? 's' : ''}</span>
                </div>
              </div>
            );
          })()}

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-foreground-500 text-[11px] font-medium">Almacenamiento</span>
              <span className="text-foreground-400 text-[11px]">{radio.storageUsedGB} GB / {radio.storageTotalGB} GB</span>
            </div>
            <div className="h-2 bg-background-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${storagePercent > 80 ? 'bg-red-500' : storagePercent > 50 ? 'bg-amber-400' : 'bg-primary-500'}`}
                style={{ width: `${Math.min(storagePercent, 100)}%` }}
              ></div>
            </div>
            <p className="text-foreground-400 text-[10px] mt-1.5">{storagePercent}% usado</p>
          </div>

          <div className="bg-background-100 rounded-xl p-3 space-y-2">
            <p className="text-foreground-400 text-[10px] font-medium">Contacto</p>
            <div className="flex items-center gap-2 text-foreground-600 text-xs">
              <i className="ri-mail-line text-foreground-400"></i>{radio.ownerEmail}
            </div>
          </div>

          {radio.daysOffline > 0 && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-3 flex items-start gap-2">
              <i className="ri-alert-line text-red-500 text-sm mt-0.5 shrink-0"></i>
              <div>
                <p className="text-red-700 text-xs font-semibold">Stream caído · {radio.daysOffline} días</p>
                <p className="text-red-500 text-[10px] mt-0.5">{radio.warningMessage || 'Requiere atención.'}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ COMUNIDAD ══ */}
      {activeTab === 'comunidad' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <CommunityStatCard icon="ri-user-heart-line" label="Seguidores" value={stats.followers} color="text-rose-500" />
            <CommunityStatCard icon="ri-article-line" label="Publicaciones" value={stats.posts} color="text-sky-500" />
            <CommunityStatCard icon="ri-chat-3-line" label="Comentarios" value={stats.comments} color="text-amber-500" />
            <CommunityStatCard icon="ri-calendar-event-line" label="Eventos" value={stats.events} color="text-violet-500" />
            <CommunityStatCard icon="ri-heart-line" label="Interacciones" value={stats.interactions} color="text-emerald-500" />
            <div className="bg-background-50 rounded-xl p-3 border border-background-200 flex flex-col items-center justify-center text-center">
              <p className="text-foreground-400 text-[10px] font-medium mb-1">Engagement Rate</p>
              <p className="text-foreground-800 text-lg font-bold">
                {stats.followers > 0 ? ((stats.interactions / stats.followers) * 100).toFixed(1) : '0'}%
              </p>
              <p className="text-foreground-300 text-[9px] mt-0.5">interacciones / seguidores</p>
            </div>
          </div>

          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-400 text-[10px] font-medium mb-3">Actividad de la comunidad</p>
            <div className="space-y-2.5">
              {[
                { label: 'Seguidores', value: stats.followers, max: 20000, color: 'bg-rose-500' },
                { label: 'Publicaciones', value: stats.posts, max: 600, color: 'bg-sky-500' },
                { label: 'Comentarios', value: stats.comments, max: 3000, color: 'bg-amber-500' },
                { label: 'Eventos', value: stats.events, max: 40, color: 'bg-violet-500' },
                { label: 'Interacciones', value: stats.interactions, max: 30000, color: 'bg-emerald-500' },
              ].map((item) => (
                <div key={item.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-foreground-500 text-[10px]">{item.label}</span>
                    <span className="text-foreground-600 text-[10px] font-semibold">{item.value.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 bg-background-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${item.color}`}
                      style={{ width: `${Math.min((item.value / item.max) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ FACTURACIÓN ══ */}
      {activeTab === 'facturacion' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-background-100 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-primary-50 flex items-center justify-center"><i className="ri-stack-line text-primary-500 text-sm"></i></div>
                <p className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Plan</p>
              </div>
              <p className="text-foreground-800 text-sm font-bold">{planFullNames[radio.plan] || radio.plan}</p>
              {relatedContract && <p className="text-foreground-400 text-[10px] mt-0.5">${relatedContract.monthlyFee === 0 ? 'Gratis' : `${relatedContract.monthlyFee}/mes`}</p>}
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-sky-50 flex items-center justify-center"><i className="ri-key-2-line text-sky-500 text-sm"></i></div>
                <p className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Licencia Anual</p>
              </div>
              {relatedContract && relatedContract.annualLicense > 0 ? (
                <>
                  <p className="text-foreground-800 text-sm font-bold">${relatedContract.annualLicense}</p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">Licencia de Frecuencia RFW</p>
                </>
              ) : (
                <p className="text-foreground-400 text-xs italic">Sin licencia anual</p>
              )}
            </div>
          </div>

          <div className="bg-background-100 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-accent-50 flex items-center justify-center"><i className="ri-bank-card-line text-accent-500 text-sm"></i></div>
              <p className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Estado Billing</p>
            </div>
            {relatedBilling ? (
              <div className="space-y-1.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-medium ${billingStatusLabels[relatedBilling.status]?.color || 'bg-foreground-100 text-foreground-500'}`}>
                  <i className={`${billingStatusLabels[relatedBilling.status]?.icon || 'ri-question-line'} text-[9px]`}></i>
                  {billingStatusLabels[relatedBilling.status]?.label || relatedBilling.status}
                </span>
                <div className="flex items-center gap-4 text-foreground-400 text-[10px]">
                  <span>Inicio: {relatedBilling.startedAt}</span><span>Próx: {relatedBilling.nextBilling}</span>
                </div>
              </div>
            ) : (
              <span className="text-foreground-400 text-xs italic">Sin datos de billing</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-background-100 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center"><i className="ri-file-list-3-line text-amber-500 text-sm"></i></div>
                <p className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Invoice Actual</p>
              </div>
              {currentInvoice ? (
                <div className="space-y-1">
                  <p className="text-foreground-800 text-xs font-mono font-semibold">{currentInvoice.number}</p>
                  <p className="text-foreground-600 text-xs font-bold">${currentInvoice.total.toFixed(2)}</p>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium ${currentInvoice.status === 'paid' ? 'bg-emerald-50 text-emerald-600' : currentInvoice.status === 'pending' ? 'bg-amber-50 text-amber-600' : currentInvoice.status === 'overdue' ? 'bg-red-50 text-red-500' : 'bg-background-200 text-foreground-500'}`}>
                    {currentInvoice.status === 'paid' ? 'Pagada' : currentInvoice.status === 'pending' ? 'Pendiente' : currentInvoice.status === 'overdue' ? 'Vencida' : currentInvoice.status}
                  </span>
                </div>
              ) : (
                <span className="text-foreground-400 text-xs italic">Sin invoices</span>
              )}
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center"><i className="ri-money-dollar-circle-line text-emerald-500 text-sm"></i></div>
                <p className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Último Pago</p>
              </div>
              {lastPayment ? (
                <div className="space-y-1">
                  <p className="text-foreground-800 text-xs font-mono font-semibold">{lastPayment.reference}</p>
                  <p className="text-foreground-600 text-xs font-bold">${lastPayment.amount.toFixed(2)}</p>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium ${lastPayment.status === 'paid' ? 'bg-emerald-50 text-emerald-600' : lastPayment.status === 'pending' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-500'}`}>
                    {lastPayment.status === 'paid' ? 'Pagado' : lastPayment.status === 'pending' ? 'Pendiente' : lastPayment.status}
                  </span>
                </div>
              ) : (
                <span className="text-foreground-400 text-xs italic">Sin pagos</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ CONTRATO ══ */}
      {activeTab === 'contrato' && (
        <div className="space-y-3">
          <div className="bg-background-100 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center"><i className="ri-file-text-line text-amber-500 text-lg"></i></div>
              <div>
                <p className="text-foreground-800 text-sm font-bold">Contrato {radio.name}</p>
                <p className="text-foreground-400 text-[10px]">Plan {planFullNames[radio.plan] || radio.plan}</p>
              </div>
            </div>
            {relatedContract ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-background-50 rounded-lg p-2.5">
                    <p className="text-foreground-400 text-[9px]">Número</p>
                    <p className="text-foreground-800 font-mono font-semibold">{relatedContract.contractNumber}</p>
                  </div>
                  <div className="bg-background-50 rounded-lg p-2.5">
                    <p className="text-foreground-400 text-[9px]">Estado</p>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium ${relatedContract.status === 'signed' ? 'bg-emerald-50 text-emerald-600' : relatedContract.status === 'pending_signature' ? 'bg-amber-50 text-amber-600' : relatedContract.status === 'draft' ? 'bg-background-200 text-foreground-500' : 'bg-red-50 text-red-500'}`}>
                      {relatedContract.status === 'signed' ? 'Firmado' : relatedContract.status === 'pending_signature' ? 'Pend. Firma' : relatedContract.status === 'draft' ? 'Borrador' : relatedContract.status === 'expired' ? 'Expirado' : relatedContract.status === 'needs_revision' ? 'Revisión' : 'Cancelado'}
                    </span>
                  </div>
                  <div className="bg-background-50 rounded-lg p-2.5">
                    <p className="text-foreground-400 text-[9px]">Mensualidad</p>
                    <p className="text-foreground-800 font-semibold">${relatedContract.monthlyFee === 0 ? 'Gratis' : relatedContract.monthlyFee}</p>
                  </div>
                  <div className="bg-background-50 rounded-lg p-2.5">
                    <p className="text-foreground-400 text-[9px]">Storage</p>
                    <p className="text-foreground-800 font-semibold">{relatedContract.storageIncluded}</p>
                  </div>
                  <div className="bg-background-50 rounded-lg p-2.5">
                    <p className="text-foreground-400 text-[9px]">Próx. Renovación</p>
                    <p className="text-foreground-800 font-semibold">{relatedContract.nextRenewal || '—'}</p>
                  </div>
                  <div className="bg-background-50 rounded-lg p-2.5">
                    <p className="text-foreground-400 text-[9px]">Licencia Anual</p>
                    <p className="text-foreground-800 font-semibold">{relatedContract.annualLicense > 0 ? `$${relatedContract.annualLicense}` : '—'}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <i className="ri-information-line text-foreground-300 text-xs"></i>
                <span className="text-foreground-400 text-xs italic">Sin contrato registrado</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══ ACTIVIDAD ══ */}
      {activeTab === 'actividad' && (
        <div className="space-y-3">
          <div className="bg-background-100 rounded-xl p-4">
            <p className="text-foreground-400 text-[10px] font-medium mb-3">Historial de estados ({radio.statusHistory.length} cambios)</p>
            <div className="max-h-80 overflow-y-auto">
              <StatusTimeline history={radio.statusHistory} />
            </div>
          </div>
        </div>
      )}
    </ModalBase>
  );
}